import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Users, BarChart3, Settings,
  RefreshCw, ChevronDown
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

interface Framework {
  assignment_id: number; id: number; name: string; code: string;
  status: string; completion_percentage: number; assigned_at: string;
}

interface User {
  id: number; first_name: string; last_name: string; email: string;
  is_active: boolean; last_login_at: string | null;
}

interface CompanyDetail {
  id: number; name: string; legal_name: string | null; industry: string | null;
  size: string | null; email: string | null; phone: string | null;
  website: string | null; is_active: boolean; created_at: string;
}

const TABS = [
  { id: 'frameworks', label: 'Frameworks', icon: BarChart3 },
  { id: 'users',      label: 'Users',      icon: Users },
  { id: 'settings',   label: 'Settings',   icon: Settings }
] as const;

type Tab = (typeof TABS)[number]['id'];

function statusColor(s: string) {
  if (s === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (s === 'active')    return 'bg-blue-100 text-blue-700';
  if (s === 'pending')   return 'bg-amber-100 text-amber-700';
  return 'bg-slate-100 text-slate-600';
}

export default function PartnerCompanyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const companyId = parseInt(id!);

  const [company, setCompany] = useState<CompanyDetail | null>(null);
  const [frameworks, setFrameworks] = useState<Framework[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [tab, setTab] = useState<Tab>('frameworks');
  const [selectedFramework, setSelectedFramework] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [form, setForm] = useState({ legal_name: '', industry: '', size: '', email: '', phone: '', website: '' });

  useEffect(() => { if (companyId && !isNaN(companyId)) load(); }, [companyId]);

  async function load() {
    setLoading(true);
    try {
      const res = await fetch(`/api/partner/companies/${companyId}`, { credentials: 'include' }).then(r => r.json());
      const co = res.company || null;
      setCompany(co);
      if (co) setForm({ legal_name: co.legal_name || '', industry: co.industry || '', size: co.size || '', email: co.email || '', phone: co.phone || '', website: co.website || '' });
      const fwList = Array.isArray(res.frameworks) ? res.frameworks : [];
      setFrameworks(fwList);
      setUsers(Array.isArray(res.users) ? res.users : []);
      if (fwList.length > 0) setSelectedFramework(fwList[0].id);
    } finally {
      setLoading(false);
    }
  }

  if (!companyId || isNaN(companyId)) {
    return <div className="p-6 text-slate-500">Invalid company ID.</div>;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (!company) {
    return (
      <div className="p-6 text-slate-500">Company not found or you don't have access.</div>
    );
  }

  const activeFramework = frameworks.find(f => f.id === selectedFramework);

  return (
    <div className="p-6 max-w-screen-xl mx-auto space-y-6">
      {/* Back + header */}
      <div>
        <button
          onClick={() => navigate('/partner/companies')}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to companies
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Building2 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">{company.name}</h1><PageInfoButton /></div>
              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500">
                {company.industry && <span>{company.industry}</span>}
                {company.size && <span className="capitalize">• {company.size}</span>}
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${company.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                  {company.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-100">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Frameworks tab */}
      {tab === 'frameworks' && (
        <div className="space-y-4">
          {frameworks.length === 0 ? (
            <div className="py-12 bg-white rounded-3xl border border-slate-100 text-slate-400">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No frameworks assigned yet.</p>
            </div>
          ) : (
            <>
              {/* Framework switcher */}
              {frameworks.length > 1 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Viewing:</span>
                  <div className="relative">
                    <select
                      value={selectedFramework ?? ''}
                      onChange={e => setSelectedFramework(parseInt(e.target.value))}
                      className="appearance-none pl-3 pr-8 py-1.5 text-sm border border-slate-200 rounded-lg bg-white font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {frameworks.map(f => (
                        <option key={f.id} value={f.id}>{f.name} ({f.code})</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              )}

              {/* All frameworks list */}
              <div className="grid gap-3">
                {frameworks.map(fw => (
                  <div
                    key={fw.id}
                    className={`bg-white rounded-xl border p-4 flex items-center gap-4 cursor-pointer transition-all ${
                      selectedFramework === fw.id ? 'border-blue-300 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => setSelectedFramework(fw.id)}
                  >
                    <div className="font-mono text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded shrink-0">{fw.code}</div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">{fw.name}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusColor(fw.status)}`}>
                          {fw.status}
                        </span>
                        <span className="text-xs text-slate-400">Assigned {new Date(fw.assigned_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="w-28 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${fw.completion_percentage >= 75 ? 'bg-emerald-500' : fw.completion_percentage >= 40 ? 'bg-amber-500' : 'bg-red-400'}`}
                          style={{ width: `${Math.min(100, fw.completion_percentage)}%` }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-slate-700 w-10 text-right">{Math.round(fw.completion_percentage)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Users tab */}
      {tab === 'users' && (
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
          {users.length === 0 ? (
            <div className="py-12 text-slate-400">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No users in this company yet.</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Email</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Last Login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {users.map(u => (
                  <tr key={u.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-left font-medium text-slate-900">{u.first_name} {u.last_name}</td>
                    <td className="px-4 py-3 text-left text-slate-500">{u.email}</td>
                    <td className="px-4 py-3 text-left">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-left text-slate-400 text-xs">
                      {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Settings tab */}
      {tab === 'settings' && (
        <form
          className="bg-white rounded-3xl border border-slate-100 p-6 space-y-5"
          onSubmit={async e => {
            e.preventDefault();
            setSaving(true); setSaveError(null); setSaveSuccess(false);
            try {
              const res = await fetch(`/api/partner/companies/${companyId}`, {
                method: 'PUT', credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form),
              });
              if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Save failed'); }
              setCompany(c => c ? { ...c, ...form, legal_name: form.legal_name || null, industry: form.industry || null, size: form.size || null, email: form.email || null, phone: form.phone || null, website: form.website || null } : c);
              setSaveSuccess(true);
              setTimeout(() => setSaveSuccess(false), 3000);
            } catch (err: any) {
              setSaveError(err.message);
            } finally {
              setSaving(false);
            }
          }}
        >
          <h3 className="font-semibold text-slate-900">Company Details</h3>
          <div className="grid grid-cols-2 gap-4">
            {([
              ['legal_name', 'Legal Name', 'text'],
              ['industry',   'Industry',   'text'],
              ['size',       'Size',       'text'],
              ['email',      'Email',      'email'],
              ['phone',      'Phone',      'tel'],
              ['website',    'Website',    'url'],
            ] as [keyof typeof form, string, string][]).map(([key, label, type]) => (
              <div key={key}>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">{label}</label>
                <input
                  type={type}
                  value={form[key]}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder={label}
                />
              </div>
            ))}
          </div>
          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
          {saveSuccess && <p className="text-sm text-emerald-600">Changes saved.</p>}
          <div className="pt-1">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-violet-600 text-white text-sm font-medium rounded-lg hover:bg-violet-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
